"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Edit, Trash2, Eye, ArrowUpDown } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { Vehicle, Customer } from "@/types";

interface VehiclesTableProps {
  vehicles: Vehicle[];
  customers: Customer[];
  sortConfig: {
    key: keyof Vehicle;
    direction: "asc" | "desc";
  };
  onSort: (key: keyof Vehicle) => void;
  onView: (vehicle: Vehicle) => void;
  onEdit: (vehicle: Vehicle) => void;
  onDelete: (vehicle: Vehicle) => void;
}

export function VehiclesTable({
  vehicles,
  customers,
  sortConfig,
  onSort,
  onView,
  onEdit,
  onDelete,
}: VehiclesTableProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Vehicles ({vehicles.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSort("plateNumber")}
                >
                  Plate Number <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSort("make")}
                >
                  Make <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onSort("model")}
                >
                  Model <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>Year</TableHead>
              <TableHead>Color</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Last Service</TableHead>
              <TableHead>Total Services</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {vehicles.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="text-center text-muted-foreground"
                >
                  No vehicles found
                </TableCell>
              </TableRow>
            ) : (
              vehicles.map((vehicle) => {
                const customer = customers.find(
                  (c) => c.id === vehicle.customerId
                );

                return (
                  <TableRow key={vehicle.id}>
                    <TableCell className="font-medium">
                      {vehicle.plateNumber}
                    </TableCell>
                    <TableCell>{vehicle.make}</TableCell>
                    <TableCell>{vehicle.model}</TableCell>
                    <TableCell>{vehicle.year ? vehicle.year : "-"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div
                          className="h-4 w-4 rounded-full border"
                          style={{
                            backgroundColor: vehicle.color.toLowerCase(),
                          }}
                        />
                        {vehicle.color}
                      </div>
                    </TableCell>
                    <TableCell>{customer?.name || "Unknown"}</TableCell>
                    <TableCell>
                      {vehicle.lastService
                        ? formatDate(vehicle.lastService)
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{vehicle.totalServices}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onView(vehicle)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEdit(vehicle)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDelete(vehicle)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

